/**
 * Popup + attribution tracking.
 *
 * Every event lands in `window.dataLayer` (Google Tag Manager container
 * GTM-KG2Z25X, injected in gatsby-ssr.js). GTM must have Custom Event triggers
 * for the event names below to forward them to GA4 / ads platforms.
 *
 * Funnel:  traffic source → landing page → popup_impression → popup_cta_click
 *          → case study page → popup_form_submission
 *
 * Session attribution (landing page, referrer, UTM params) is captured once per
 * browser session in sessionStorage (see captureSessionAttribution, called from
 * gatsby-browser.js), so every later event can be tied back to where the visitor
 * came from — even after client-side navigation strips the UTM params.
 *
 * Events and their payload (all keys are always present so GTM never keeps a
 * stale value from a previous push):
 *
 *   popup_impression        popup_name, popup_variant, page_url, landing_page,
 *                           referrer, utm_source, utm_medium, utm_campaign,
 *                           utm_content, utm_term
 *   popup_cta_click         (same) + cta_url
 *   popup_dismiss           (same) + dismiss_method: close_button | overlay | escape
 *   popup_form_submission   (same) + form_name, popup_cta_page_url, popup_cta_clicked_at
 *                           — fired ONLY when the popup CTA was clicked earlier in
 *                           the same session, from the form's success handler.
 */

const STORAGE_KEYS = {
  attribution: "alpacked_session_attribution",
  popupShown: "alpacked_popup_shown",
  popupCta: "alpacked_popup_cta",
}

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
]

/** Popup definitions. `name` is what shows up as `popup_name` in GTM. */
export const CASE_STUDY_POPUP = {
  name: "igaming_case_study",
  ctaUrl: "/case-studies/automated-domain-management/",
}

/* ------------------------------------------------------------------ */
/* Storage (sessionStorage with in-memory fallback for private modes)   */
/* ------------------------------------------------------------------ */

const memoryStore = {}

const isBrowser = () => typeof window !== "undefined"

const readStorage = key => {
  try {
    if (isBrowser() && window.sessionStorage) {
      const value = window.sessionStorage.getItem(key)
      if (value !== null) return value
    }
  } catch (_) {
    // sessionStorage can throw (Safari private mode, disabled storage) — fall through
  }
  return Object.prototype.hasOwnProperty.call(memoryStore, key)
    ? memoryStore[key]
    : null
}

const writeStorage = (key, value) => {
  memoryStore[key] = value
  try {
    if (isBrowser() && window.sessionStorage) {
      window.sessionStorage.setItem(key, value)
    }
  } catch (_) {
    // ignore — memory fallback already holds the value for this page
  }
}

const readJSON = key => {
  const raw = readStorage(key)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch (_) {
    return null
  }
}

const writeJSON = (key, value) => writeStorage(key, JSON.stringify(value))

/* ------------------------------------------------------------------ */
/* Session attribution                                                  */
/* ------------------------------------------------------------------ */

const getCurrentUrl = () => (isBrowser() ? window.location.href : "")

const readUtmParams = () => {
  const result = {}
  let params = null
  try {
    params = new URLSearchParams(window.location.search)
  } catch (_) {
    params = null
  }
  UTM_KEYS.forEach(key => {
    result[key] = params ? params.get(key) || "" : ""
  })
  return result
}

/**
 * Capture landing page / referrer / UTM params for this session.
 * Runs on first load of the session; a later full page load that carries UTM
 * params (a new campaign click in the same tab) overwrites the earlier touch.
 */
export const captureSessionAttribution = () => {
  if (!isBrowser()) return null

  const existing = readJSON(STORAGE_KEYS.attribution)
  const utm = readUtmParams()
  const hasUtm = UTM_KEYS.some(key => utm[key])

  if (existing && !hasUtm) return existing

  const attribution = {
    landing_page: window.location.href,
    referrer: (typeof document !== "undefined" && document.referrer) || "",
    captured_at: new Date().toISOString(),
    ...utm,
  }
  writeJSON(STORAGE_KEYS.attribution, attribution)
  return attribution
}

export const getSessionAttribution = () =>
  readJSON(STORAGE_KEYS.attribution) || captureSessionAttribution() || {}

/* ------------------------------------------------------------------ */
/* dataLayer                                                            */
/* ------------------------------------------------------------------ */

export const pushToDataLayer = payload => {
  if (!isBrowser()) return
  try {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(payload)
  } catch (_) {
    // never let analytics break the UI
  }
}

const buildPopupPayload = (event, popupName, variant, extra = {}) => {
  const attribution = getSessionAttribution()
  return {
    event,
    popup_name: popupName,
    popup_variant: variant,
    page_url: getCurrentUrl(),
    landing_page: attribution.landing_page || "",
    referrer: attribution.referrer || "",
    utm_source: attribution.utm_source || "",
    utm_medium: attribution.utm_medium || "",
    utm_campaign: attribution.utm_campaign || "",
    utm_content: attribution.utm_content || "",
    utm_term: attribution.utm_term || "",
    cta_url: undefined,
    dismiss_method: undefined,
    form_name: undefined,
    popup_cta_page_url: undefined,
    popup_cta_clicked_at: undefined,
    ...extra,
  }
}

/* ------------------------------------------------------------------ */
/* Popup state                                                          */
/* ------------------------------------------------------------------ */

const shownKey = popup => `${STORAGE_KEYS.popupShown}:${popup.name}`
const ctaKey = popup => `${STORAGE_KEYS.popupCta}:${popup.name}`

export const hasPopupBeenShown = popup => readStorage(shownKey(popup)) === "1"

export const markPopupShown = popup => writeStorage(shownKey(popup), "1")

export const getPopupCtaClick = popup => readJSON(ctaKey(popup))

/* ------------------------------------------------------------------ */
/* Popup events                                                         */
/* ------------------------------------------------------------------ */

export const trackPopupImpression = (popup, variant) => {
  pushToDataLayer(buildPopupPayload("popup_impression", popup.name, variant))
}

export const trackPopupCtaClick = (popup, variant) => {
  const click = {
    popup_name: popup.name,
    popup_variant: variant,
    page_url: getCurrentUrl(),
    clicked_at: new Date().toISOString(),
  }
  // Remembered for the rest of the session so a later form submission
  // (e.g. on the case study page) can be attributed to this popup.
  writeJSON(ctaKey(popup), click)

  pushToDataLayer(
    buildPopupPayload("popup_cta_click", popup.name, variant, {
      cta_url: popup.ctaUrl,
    })
  )
}

export const trackPopupDismiss = (popup, variant, method) => {
  pushToDataLayer(
    buildPopupPayload("popup_dismiss", popup.name, variant, {
      dismiss_method: method,
    })
  )
}

/**
 * Call from any form's success handler. Fires `popup_form_submission` only if
 * a popup CTA was clicked earlier in this session; otherwise it is a no-op.
 * Returns true when an event was pushed.
 */
export const trackPopupFormSubmission = formName => {
  const popups = [CASE_STUDY_POPUP]
  let tracked = false

  popups.forEach(popup => {
    const click = getPopupCtaClick(popup)
    if (!click) return
    pushToDataLayer(
      buildPopupPayload("popup_form_submission", popup.name, click.popup_variant, {
        form_name: formName,
        popup_cta_page_url: click.page_url || "",
        popup_cta_clicked_at: click.clicked_at || "",
      })
    )
    tracked = true
  })

  return tracked
}
